<template>
    <div class="corporate-student-sidebar tw:flex tw:flex-col tw:h-full">
        <div class="tw:flex-1 tw:min-h-0">
            <sidebar-menu
                :items="items"
                :include-home="false"
                :collapse-on-navigate="false"
                has-icon
                @update:expanded="$emit('update:expanded', $event)"
            >
                <!--
                    Expanded (tree) mode: PrimeVue Tree's row template uses
                    SidebarMenu's `#nodeIcon` slot to render the per-item
                    icon next to the label.
                -->
                <template #nodeIcon="{ node }">
                    <component
                        :is="node.data.iconComponent"
                        v-if="node.data?.iconComponent"
                        :size="18"
                        weight="regular"
                        class="tw:mr-2 tw:inline-block tw:align-middle"
                    />
                </template>

                <!--
                    Collapsed (summary) mode: without this slot,
                    AppSidebarSummaryItem would receive an undefined
                    `:icon` and fall back to SidebarMenu's `'link'` default,
                    causing every collapsed student item to render as the
                    same chain-link Unicons glyph. Providing
                    `#summaryIcon` replaces the default `<e-icon>` glyph
                    with the same Phosphor SFC we render in expanded mode
                    (via `iconComponent`), so the two states stay visually
                    consistent. `iconComponent` lives on the original item
                    payload, surfaced here as `item.data.iconComponent`
                    (SidebarMenu's convertItemsToNodes spreads each input
                    item into `node.data`).
                -->
                <template #summaryIcon="{ item }">
                    <component
                        :is="item.data.iconComponent"
                        v-if="item.data?.iconComponent"
                        :size="18"
                        weight="regular"
                    />
                </template>
            </sidebar-menu>
        </div>
    </div>
</template>

<script>
import { PhBookOpenText, PhBooks, PhCertificate, PhHouse } from '@phosphor-icons/vue';
import SidebarMenu from '@/components/sidebar/SidebarMenu.vue';

export default {
    name: 'CorporateStudentSidebar',

    components: { SidebarMenu },

    emits: ['update:expanded'],

    computed: {
        items() {
            // Corporate student navigation. Mirrors the structure of the
            // admin sidebar (same SidebarMenu + items[] shape) so the role
            // resolver in CorporateSidebar.vue can swap variants without
            // either side knowing about the other.
            //
            // All four destinations are real named routes:
            //   - `home` is the shared dashboard root.
            //   - `corporate-my-courses` renders MyCourses.vue.
            //   - `corporate-course-library` and `corporate-certificates`
            //     render their respective "Coming Soon" placeholder pages
            //     until the underlying flows land. Routing to a real named
            //     page (vs. the old `{ path: '#' }` sentinel) means the URL
            //     reflects intent on click, breadcrumbs hydrate correctly,
            //     and the page can grow into its final form without any
            //     sidebar churn.
            //
            // Because CorporateStudentSidebar opts out of
            // `collapseOnNavigate`, the expanded menu stays visible after
            // the student clicks through.
            //
            // Icons are Phosphor components rendered via SidebarMenu's
            // `#nodeIcon` slot. `iconComponent` is intentionally used
            // instead of `icon` so the PrimeVue Tree does not also try to
            // render a `pi pi-...` glyph next to the Phosphor SVG.
            return [
                // {
                //     type: 'item',
                //     order: 1,
                //     title: 'Home',
                //     iconComponent: PhHouse,
                //     // Points at the shared `home` route (`/`) rather than
                //     // `corporate-my-courses`. Both items used to resolve to
                //     // the same path, which made `router-link-exact-active`
                //     // light up on Home AND My Courses simultaneously
                //     // whenever a student landed on /my-courses. Routing
                //     // Home through `/` lets the temp CorporateStudentDashboard
                //     // redirect forward the student onward without ever
                //     // matching the active-link rules here.
                //     route: { name: 'home' },
                // },
                {
                    type: 'item',
                    order: 1,
                    title: 'My Courses',
                    iconComponent: PhBookOpenText,
                    route: { name: 'corporate-my-courses' },
                },
                {
                    type: 'item',
                    order: 2,
                    title: 'Course Library',
                    iconComponent: PhBooks,
                    route: { name: 'corporate-course-library' },
                },
                {
                    type: 'item',
                    order: 3,
                    title: 'My Certificates',
                    iconComponent: PhCertificate,
                    route: { name: 'corporate-certificates' },
                },
            ];
        },
    },
};
</script>

<style scoped lang="scss">
// SidebarMenu's shared `<aside>` carries Bulma's `has-text-weight-semibold`
// utility (font-weight: 600 !important), which made every item in this
// menu read as semibold and visually flattened the contrast with the
// active item. We can't just drop the Bulma class from the shared file —
// it's also used by every Stanbridge sidebar variant — so we override
// here, scoped to the corporate student sidebar only. `!important` is
// required because Bulma's utility itself uses `!important`.
:deep(#application_sidebar.has-text-weight-semibold) {
    font-weight: normal !important;
}

// SidebarMenu's global `.tree-node-link.router-link-active` rule uses the
// INCLUSIVE active class — which matches whenever the link's resolved
// path is a prefix of the current URL. Two consequences for this
// sidebar:
//
//   - The Home item resolves to `/` (see router/ModularRoutes.js), so
//     `router-link-active` lands on it for every page in the app, since
//     every URL starts with `/`.
//   - Course Library and Certificates are wired to `{ path: '#' }`
//     placeholders, whose resolved path is also an always-match prefix.
//
// Net effect: three items would render as "active = bold" everywhere,
// completely defeating the cue we want for the actual current page. The
// fix is to neutralize the inclusive rule on this sidebar and re-bold
// only on `.router-link-exact-active`, which fires only when the URL
// equals the link's resolved path. Stanbridge sidebars keep inclusive
// matching (their nested course/section/activity routes legitimately
// want the parent menu item highlighted while inside a sub-route).
:deep(.tree-node-link.router-link-active) {
    font-weight: normal;
}

// Active destination is highlighted in the brand/primary colour (Figma shows
// #3b82f6; we use the theme primary so per-institution primary_color theming is
// respected). Phosphor icons render with `fill="currentColor"`, so colouring
// the link recolours both the label and its icon. Mirrors the collapsed rail's
// primary-coloured active treatment in AppSidebarSummaryItem.
:deep(.tree-node-link.router-link-exact-active) {
    font-weight: bold;
    color: var(--p-primary-color);
}
</style>
